import { showToast } from '../../../engine/toast';
import {
  adminListPlayers,
  adminBanUser,
  adminUnbanUser,
  adminVerifyEmail,
  listItems,
  type AdminUser,
} from '../admin-api';
import { escapeHtml, BTN_CLS, INPUT_CLS, SMALL_BTN_CLS } from '../admin-ui';
import { openCreateUser } from '../modals/create-user';
import { openEditPlayer } from '../modals/edit-player';
import { openKycReview } from '../modals/kyc-review';
import { openAddCredits } from '../modals/add-credits';
import type { PaneCtx, PaneModule } from './types';

const KYC_CHIP: Record<string, string> = {
  APPROVED: 'text-success',
  REJECTED: 'text-crimson',
  PENDING: 'text-gold',
  NONE: 'text-text-dim',
};

function rowHtml(u: AdminUser): string {
  return `
    <tr class="border-t border-border" data-user-row="${u.id}">
      <td class="py-2 pr-2">
        <div class="text-sm text-text truncate">${escapeHtml(u.username ?? '—')}</div>
        <div class="text-[10px] text-text-dim truncate">${escapeHtml(u.email)}</div>
      </td>
      <td class="py-2 pr-2 text-[10px] whitespace-nowrap">
        <span class="text-text-dim">${escapeHtml(u.role)}</span>
        ${u.banned ? ' · <span class="text-crimson">banned</span>' : ''}
        ${u.emailVerified ? '' : ' · <span class="text-crimson">unverified</span>'}
        · <span class="${KYC_CHIP[u.kycStatus] ?? 'text-text-dim'}">${escapeHtml(u.kycStatus)}</span>
      </td>
      <td class="py-2 text-right whitespace-nowrap">
        <button type="button" class="${SMALL_BTN_CLS}" data-action="edit-player" data-id="${u.id}">Edit</button>
        <button type="button" class="${SMALL_BTN_CLS} ml-1" data-action="review-kyc" data-id="${u.id}">KYC</button>
        <button type="button" class="${SMALL_BTN_CLS} ml-1" data-action="add-credits" data-id="${u.id}">Credits</button>
        <button type="button" class="${SMALL_BTN_CLS} ml-1" data-action="${u.banned ? 'unban' : 'ban'}" data-id="${u.id}">${u.banned ? 'Unban' : 'Ban'}</button>
        ${u.emailVerified ? '' : `<button type="button" class="${SMALL_BTN_CLS} ml-1" data-action="verify-email" data-id="${u.id}">Verify email</button>`}
      </td>
    </tr>`;
}

/** Row actions act on a player, so every one needs the loaded record. */
function findUser(ctx: PaneCtx, id: string): AdminUser | undefined {
  return ctx.state.players.items.find((u) => u.id === id);
}

export const playersPane: PaneModule = {
  id: 'players',
  label: 'Players',

  html(ctx) {
    const rows = ctx.state.players.items.map(rowHtml).join('');
    return `
      <section class="flex flex-col gap-2">
        <div class="flex items-center justify-between gap-2">
          <h2 class="font-heading text-xs text-gold tracking-wide uppercase">Players</h2>
          <button type="button" class="${SMALL_BTN_CLS}" data-action="create-user">+ Create user</button>
        </div>
        <form data-form="user-search" class="flex gap-2">
          <input name="search" type="search" value="${escapeHtml(ctx.state.players.search)}"
            placeholder="Search name, email or id" class="${INPUT_CLS}" />
          <button type="submit" class="${BTN_CLS}">Search</button>
        </form>
        <div class="glass-panel px-3 py-2 overflow-x-auto">
          <table class="w-full text-left"><tbody>${rows || '<tr><td class="py-3 text-xs text-text-dim">No players found.</td></tr>'}</tbody></table>
        </div>
      </section>`;
  },

  async load(ctx) {
    // The game-owned list, not core's: this one also matches an exact id,
    // which is what makes the search box's "or id" promise true.
    ctx.state.players.items = listItems(await adminListPlayers({ search: ctx.state.players.search || undefined }));
  },

  onAction(action, el, ctx) {
    const id = el.dataset.id ?? '';

    if (action === 'create-user') {
      openCreateUser(ctx);
      return true;
    }
    if (action === 'edit-player' || action === 'review-kyc' || action === 'add-credits') {
      const user = findUser(ctx, id);
      if (!user) return true;
      if (action === 'edit-player') openEditPlayer(ctx, user);
      else if (action === 'review-kyc') openKycReview(ctx, user);
      else openAddCredits(ctx, user);
      return true;
    }
    if (action === 'ban' || action === 'unban') {
      void ctx.withBusy(async () => {
        await (action === 'ban' ? adminBanUser(id) : adminUnbanUser(id));
        showToast(action === 'ban' ? 'Player banned.' : 'Player unbanned.', 'success');
        await ctx.reload('players');
      }, 'Could not change that player.');
      return true;
    }
    if (action === 'verify-email') {
      void ctx.withBusy(async () => {
        await adminVerifyEmail(id);
        showToast('Email marked verified.', 'success');
        await ctx.reload('players');
      }, 'Could not verify that email.');
      return true;
    }
    return false;
  },

  onSubmit(kind, form, ctx) {
    if (kind !== 'user-search') return false;
    ctx.state.players.search = String(new FormData(form).get('search') ?? '').trim();
    void ctx.reload('players');
    return true;
  },
};
